// FILE: packages/components/src/launch_site/homepage/agents/AgentsPreviewSection.tsx
// Enhanced AgentsPreviewSection: now a horizontal scrollable carousel

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button, Badge } from '@dopomogai/ui';
import { AgentCard } from '@dopomogai/ui';
import type { AgentCardData } from '@dopomogai/ui';

type Category = { id: string; label?: string; labelKey?: string };

type Accent =
  | 'emerald' | 'violet' | 'blue' | 'cyan' | 'amber' | 'rose'
  | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia'
  | 'pink' | 'slate' | 'gray';

const stripeClass = (accent?: Accent) => {
  switch (accent) {
    case 'violet': return 'bg-violet-400';
    case 'blue': return 'bg-blue-400';
    case 'cyan': return 'bg-cyan-400';
    case 'amber': return 'bg-amber-400';
    case 'rose': return 'bg-rose-400';
    case 'indigo': return 'bg-indigo-400';
    case 'purple': return 'bg-purple-400';
    case 'teal': return 'bg-teal-400';
    case 'orange': return 'bg-orange-400';
    case 'lime': return 'bg-lime-400';
    case 'fuchsia': return 'bg-fuchsia-400';
    case 'pink': return 'bg-pink-400';
    case 'slate': return 'bg-slate-400';
    case 'gray': return 'bg-gray-400';
    default: return 'bg-emerald-400';
  }
};

export type AgentsPreviewSectionProps = {
  eyebrow?: string;
  eyebrowKey?: string;
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  agents: AgentCardData[];
  categories?: Category[];
  viewAllCta?: { label?: string; labelKey?: string; actionId: string };
  onAction?: (actionId: string, payload?: any) => void;
  showTopStripe?: boolean;
  accent?: Accent;
  className?: string;
};

export const AgentsPreviewSection: React.FC<AgentsPreviewSectionProps> = ({
  eyebrow,
  eyebrowKey,
  title,
  titleKey,
  subtitle,
  subtitleKey,
  agents,
  categories = [],
  viewAllCta,
  onAction,
  showTopStripe = true,
  accent = 'emerald',
  className,
}) => {
  const { t } = useTranslation('homepage');
  const resolvedEyebrow = eyebrowKey ? t(eyebrowKey) : eyebrow;
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  // Create a lookup map from category ID to its display label.
  const categoryMap = useMemo(() => {
    return new Map(
      categories.map((c) => [c.id, c.labelKey ? t(c.labelKey) : c.label ?? c.id])
    );
  }, [categories, t]);

  // Process agents to convert their `tags` into a `badges` array that AgentCard can render.
  const processedAgents = useMemo(() => {
    return agents.map((agent) => {
      const agentTags = (agent as any).tags || [];
      const generatedBadges = agentTags
        .map((tagId: string) => categoryMap.get(tagId)) // Find the label for each tag ID
        .filter(Boolean) // Remove any tags that don't have a matching category
        .map((label: string) => ({ text: label })); // Create the badge object shape AgentCard expects

      return { ...agent, badges: generatedBadges };
    });
  }, [agents, categoryMap]);

  const handleViewAll = () => viewAllCta?.actionId && onAction?.(viewAllCta.actionId);

  return (
    <section className={clsx('relative bg-white overflow-hidden', className)}>
      {showTopStripe && (
        <div aria-hidden="true" className={clsx('absolute inset-x-2 top-2 h-2 rounded-full', stripeClass(accent))} />
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-12 md:pt-16 pb-6 md:pb-8">
        {(resolvedEyebrow || resolvedTitle || resolvedSubtitle || viewAllCta) && (
          <div className="mb-6 md:mb-8">
            {resolvedEyebrow && (
              <div className="mb-2">
                <Badge accentColor="green" text={resolvedEyebrow} />
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                {resolvedTitle && (
                  <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">{resolvedTitle}</h2>
                )}
                {resolvedSubtitle && (
                  <p className="mt-3 text-slate-700 text-base md:text-lg max-w-3xl">
                    {resolvedSubtitle}
                  </p>
                )}
              </div>
              {viewAllCta && (
                <div className="shrink-0">
                  <Button
                    variant="secondary"
                    text={viewAllCta.labelKey ? t(viewAllCta.labelKey) : (viewAllCta.label ?? 'Browse all')}
                    onClick={handleViewAll}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* The filter, search, and sort UI has been removed as requested for a cleaner look. */}
      </div>

      <div className="w-full pl-4 md:pl-6 lg:pl-8">
        <div
          className="flex gap-5 pb-12 md:pb-16 overflow-x-auto scroll-smooth snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {processedAgents.map((agent) => (
            <div
              key={agent.id}
              className="flex-shrink-0 snap-start w-[85vw] sm:w-[45vw] md:w-[30vw] max-w-sm h-auto"
            >
              <div className="h-full min-h-[520px]">
                <AgentCard
                  data={agent}
                  variant={'default'}
                  onAction={(actionType: string, payload?: any) => {
                    const actionId = `AGENT_CARD_${String(actionType || 'click').toUpperCase()}`;
                    onAction?.(actionId, { agentId: agent.id, ...payload });
                  }}
                />
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-1" />
        </div>
      </div>
    </section>
  );
};

export default AgentsPreviewSection;